import { useCallback, useRef, type RefObject } from "react";
import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import type { FigureListResponse } from "@patent-studio/api-types";
import { ApiError, refusalDetail } from "@/services/api-client";
import { deleteFile } from "@/services/files";
import {
  restoreFigure as restoreFigureOnServer,
  setAsideFigure,
} from "@/services/figures";
import { stageSourcesScope, taskDetailKey } from "./query-keys";
import type { DefinitionChange } from "./use-figure-definitions";

/**
 * The acts on a whole drawing: Delete, which sets it aside, Restore and Permanently Delete.
 *
 * Apart from the per-figure changes because each one moves the listing rather than the set:
 * the store answers with the figures as they now stand, or a delete is read back. They share
 * the definitions queue, so none of them races a write already on its way.
 */

/**
 * Which drawing this change is about, or nothing when it is about the numbering they share.
 *
 * Read off the change's own shape rather than a list of kinds, so a change added later is
 * classified by what it names and cannot be forgotten here.
 */
export function drawingOf(change: DefinitionChange): string | undefined {
  return "fileId" in change ? change.fileId : undefined;
}

function about(change: DefinitionChange, fileId: string): boolean {
  return drawingOf(change) === fileId;
}

/**
 * What a refused delete gives back.
 *
 * Only when the drawing is still listed. A delete refused because another session had already
 * removed the file would otherwise put the attorney's work back on a figure that is not there
 * to hold it, and then write it.
 */
function handBack(
  qc: QueryClient,
  key: QueryKey,
  fileId: string,
  gone: Set<string>,
  setAside: readonly DefinitionChange[],
  issue: (change: DefinitionChange) => void,
): void {
  const listed = qc.getQueryData<FigureListResponse>(key)?.figures ?? [];
  if (!listed.some((figure) => figure.file_id === fileId)) return;
  gone.delete(fileId);
  for (const change of setAside) issue(change);
}

/**
 * One act on a whole drawing, after everything already queued has settled.
 *
 * Joins the back of the queue rather than racing it, so a write that was in flight lands
 * while the drawing it names is still in the set. Answers whether the act landed, which is
 * what an Undo offered after it waits on.
 */
function enqueue(
  inFlight: RefObject<Promise<unknown>>,
  taskId: string | undefined,
  act: (task: string) => Promise<void>,
  failure: string,
  onFail?: () => void,
): Promise<boolean> {
  if (taskId === undefined) return Promise.resolve(false);
  const run = async () => {
    try {
      await act(taskId);
      return true;
    } catch (error) {
      onFail?.();
      toast.error(refusedWith(error, failure));
      return false;
    }
  };
  const done = inFlight.current.then(run, run);
  inFlight.current = done;
  return done;
}

/**
 * The words a failed act is shown with: the store's own for a refusal, which names what
 * stopped it and what to do, or the act's fallback for anything else.
 */
function refusedWith(error: unknown, failure: string): string {
  return error instanceof ApiError && error.status === 409
    ? refusalDetail(error, failure)
    : failure;
}

/** What the definitions hook lends these acts: its cache, its queue and its pending list. */
export interface DefinitionsQueue {
  qc: QueryClient;
  key: QueryKey;
  taskId: string | undefined;
  inFlight: RefObject<Promise<unknown>>;
  pending: RefObject<readonly DefinitionChange[]>;
  setPending: (changes: readonly DefinitionChange[]) => void;
  /** Drawings out of the set, whose changes are refused until they come back. */
  gone: RefObject<Set<string>>;
  issue: (change: DefinitionChange) => void;
}

export function useWholeFigureActs({
  qc,
  key,
  taskId,
  inFlight,
  pending,
  setPending,
  gone,
  issue,
}: DefinitionsQueue) {
  const queued = useCallback(
    (
      act: (task: string) => Promise<void>,
      failure: string,
      onFail?: () => void,
    ) => enqueue(inFlight, taskId, act, failure, onFail),
    [inFlight, taskId],
  );

  /**
   * Take one drawing out of the set, keeping its work for Restore.
   *
   * The figure's own pending changes are dropped, because a change to a drawing that is
   * leaving has no outcome worth writing, and handed back if the set aside fails. The store
   * owns what leaving does to the numbering, so its answer is adopted rather than folded in.
   */
  const deleteFigure = useCallback(
    (fileId: string) => {
      // Asked for already. The control stays on screen until the answer takes the figure
      // away, so a second ask is the attorney's to make, and the store would refuse it.
      if (taskId === undefined || gone.current.has(fileId))
        return Promise.resolve(false);
      const setAside = pending.current.filter((held) => about(held, fileId));
      pending.current = pending.current.filter((held) => !about(held, fileId));
      setPending(pending.current);
      gone.current.add(fileId);
      return queued(
        async (task) => {
          qc.setQueryData(key, await setAsideFigure(task, fileId));
          // A drawing out of the set is no longer one Figures and the draft draw.
          void qc.invalidateQueries({ queryKey: stageSourcesScope(task) });
        },
        "Could not delete that figure. Please try again.",
        () => {
          handBack(qc, key, fileId, gone.current, setAside, issue);
        },
      );
    },
    [gone, issue, key, pending, qc, queued, setPending, taskId],
  );

  // Drawings the store has been asked to restore, so a second press sends nothing.
  const restoringRef = useRef(new Set<string>());

  /** Put a set-aside drawing back in the set, and take changes to it again once it is back. */
  const restoreFigure = useCallback(
    (fileId: string) => {
      if (restoringRef.current.has(fileId)) return Promise.resolve(false);
      restoringRef.current.add(fileId);
      return queued(async (task) => {
        qc.setQueryData(key, await restoreFigureOnServer(task, fileId));
        gone.current.delete(fileId);
        void qc.invalidateQueries({ queryKey: stageSourcesScope(task) });
      }, "Could not restore that figure. Please try again.").finally(() => {
        restoringRef.current.delete(fileId);
      });
    },
    [gone, key, qc, queued],
  );

  // Drawings the store has been told to erase, so a second ask sends nothing.
  const purgingRef = useRef(new Set<string>());

  /** Erase a set-aside drawing for good: its file goes, as any file delete takes it. */
  const purgeFigure = useCallback(
    (fileId: string) => {
      if (purgingRef.current.has(fileId)) return Promise.resolve(false);
      purgingRef.current.add(fileId);
      return queued(
        async (task) => {
          await deleteFile(task, fileId);
          // The file leaves the draft, so every read under it asks again: the figures, the
          // draft's file list and each stage's sources.
          await qc.invalidateQueries({ queryKey: taskDetailKey(task) });
        },
        "Could not permanently delete that figure. Please try again.",
        () => {
          purgingRef.current.delete(fileId);
        },
      );
    },
    [qc, queued],
  );

  return { deleteFigure, restoreFigure, purgeFigure };
}
